import React, { useState } from 'react';
import styled from 'styled-components';
import Column from 'components/common/Column';
import Text from 'components/common/Text';
import Row from 'components/common/Row';
import Textarea from 'containers/ProductRegist/components/common/Textarea';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import {
  BookingItem,
  CancelReasonKind,
  CancelRequestParamInput,
  OrderItem,
} from 'types/api';
import HackleManager from 'utils/hackle';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { IconClose, IconWarning } from 'assets/icons';

const ModalHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  padding: 36px 20px 24px;

  > h6 {
    margin: 0;

    font-size: 20px;
    color: rgba(27, 28, 30, 1);
    font-weight: 600;
    line-height: 28px;
  }

  svg {
    min-width: 24px;
    min-height: 24px;

    cursor: pointer;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  padding: 0px 20px 48px;
`;

const WarningBox = styled.div`
  display: flex;
  gap: 12px;

  > p {
    color: #ff4242;

    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
  }

  > svg {
    min-width: 24px;
    min-height: 24px;
  }
`;

const RadioBox = styled.input`
  width: 24px;
  height: 24px;
  appearance: none;
  border: 3px solid #eee;
  border-radius: 50%;
  cursor: pointer;

  &:checked {
    border: 8px solid #1b1c1e;
  }
`;

const ModalFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  position: sticky;
  bottom: 0;
  padding: 16px 20px;
  margin-top: auto;

  width: 100%;
  background-color: inherit;
  border-radius: 0 0 16px 16px;
  border-top: 1px solid #dbdcdf;
`;

const CancelButton = styled.button<{ $isDisabled: boolean }>`
  padding: 0 12px;

  min-width: 104px;
  padding: 0 24px;

  border: none;
  border-radius: 8px;

  font-weight: 600;
  font-size: 16px;
  line-height: 48px;

  cursor: pointer;

  ${({ $isDisabled }) => `
    color:  ${$isDisabled ? 'rgba(219, 220, 223, 1)' : 'rgba(255, 66, 66, 1)'} ;
    background-color: ${
      $isDisabled ? 'rgba(244, 244, 245, 1)' : 'rgba(255, 230, 230, 1)'
    };
  `}
`;

interface CancelOrderContainerIProps {
  isMobile: boolean;
  orderItems: OrderItem;
  bookingItem: BookingItem;
  handleRefetchOrderItem: () => void;
  handleRequestCancelOrder: (param: any, callback: () => void) => void;
  onClose: () => void;
  type?: 'single' | 'all';
  handleCancelAllOrder?: (kind: CancelReasonKind, reason: string) => void;
  hasConfirmedSchedule?: boolean;
}

const CancelOrderContainer: React.FC<CancelOrderContainerIProps> = props => {
  const {
    isMobile,
    orderItems,
    bookingItem,
    handleRefetchOrderItem,
    handleRequestCancelOrder,
    onClose,
    type = 'single',
    handleCancelAllOrder,
    hasConfirmedSchedule,
  } = props;

  const [cancelOrderParam, setCancelOrderParam] =
    useState<CancelRequestParamInput>({
      orderId: bookingItem?.booking?.order?.id || '',
      items: [
        {
          orderItemId: orderItems?.id || '',
          quantity: -1,
          indices: [bookingItem?.indexNo || 0],
        },
      ],
      forced: false,
      manualRefund: false,
      kind: null,
      fullyCancel: false,
      reason: '',
      refunds: [],
      refundAccount: null,
    });

  const isConfirmedSchedule =
    type === 'all' ? hasConfirmedSchedule : bookingItem.status === 'CONFIRMED';

  const handleSetCancelOrderParam = (key: string, value: any) => {
    setCancelOrderParam({ ...cancelOrderParam, [key]: value });
  };

  const submitCancelRequest = debounce(() => {
    if (!cancelOrderParam.kind || !cancelOrderParam.reason) {
      return;
    }

    if (type === 'all') {
      handleCancelAllOrder(cancelOrderParam.kind, cancelOrderParam.reason);

      return onClose();
    }

    /*  */
    handleRequestCancelOrder(cancelOrderParam, () => {
      HackleManager.logSalesCancelRequestComplete(
        Number(orderItems?.booking.order.id),
        orderItems?.booking.order.customer.id,
        [bookingItem?.booking.id],
        orderItems?.booking.tickets.length,
        cancelOrderParam.kind === 'USER' ? '고객변심' : '호스트사정',
        cancelOrderParam.reason,
        orderItems?.info.origin.product.frip.attachedToSchedule
          ? '일정지정형'
          : '일정조율형',
        '판매상세',
      );

      handleRefetchOrderItem();

      onClose();
    });
  }, 500);

  return (
    <ModalContainer
      isAbled={
        cancelOrderParam.kind === 'USER' ||
        (cancelOrderParam.kind === 'HOST' && cancelOrderParam.reason !== '')
      }
      isMobile={isMobile}
      onAcceptText="취소 요청하기"
      isBottomSheet
      onClose={() => {}}
      onClick={() => {}}
      style={{
        gap: 0,
        // overflowY: 'hidden',
        ...(!isMobile && { maxWidth: '375px' }),
      }}
    >
      <ModalHeader>
        <h6>주문 취소는 관리자의 확인 후 취소가 완료됩니다.</h6>

        <IconClose color={'#70737C'} width={24} height={24} onClick={onClose} />
      </ModalHeader>

      <ModalBody>
        {isConfirmedSchedule && (
          <WarningBox>
            <IconWarning width="24px" height="24px" />
            <p>
              {type === 'all'
                ? '예약이 이미 확정된 건이 포함 되어있어요. 예약자와 사전 협의가 완료됐다면 취소 요청을 진행해 주세요.'
                : '예약이 이미 확정된 건이에요. 예약자와 사전 협의가 완료됐다면 취소 요청을 진행해 주세요.'}
            </p>
          </WarningBox>
        )}

        <Column gap="12px" align="flex-start">
          <Text color="#1b1c1e" size="16px">
            주문 취소 사유
          </Text>
          <Row gap="16px">
            <Row
              gap="8px"
              onClick={() => handleSetCancelOrderParam('kind', 'USER')}
            >
              <RadioBox
                type="radio"
                checked={cancelOrderParam.kind === 'USER'}
                onChange={() => handleSetCancelOrderParam('kind', 'USER')}
              />
              <Text color={cancelOrderParam.kind === 'USER' && '#1b1c1e'}>
                고객 변심
              </Text>
            </Row>
            <Row
              gap="8px"
              onClick={() => handleSetCancelOrderParam('kind', 'HOST')}
            >
              <RadioBox
                type="radio"
                checked={cancelOrderParam.kind === 'HOST'}
                onChange={() => handleSetCancelOrderParam('kind', 'HOST')}
              />
              <Text color={cancelOrderParam.kind === 'HOST' && '#1b1c1e'}>
                호스트 사정
              </Text>
            </Row>
          </Row>
        </Column>

        <Column gap="12px" align="flex-start">
          <Text color="#1b1c1e" size="16px">
            상세 사유
          </Text>
          <Textarea
            target="reason"
            value={cancelOrderParam.reason}
            placeholder="자세한 사유를 입력해 주세요"
            onChange={handleSetCancelOrderParam}
            style={{ minHeight: '110px' }}
          />
        </Column>
      </ModalBody>

      <ModalFooter>
        <CancelButton
          onClick={submitCancelRequest}
          $isDisabled={!cancelOrderParam.reason || !cancelOrderParam.kind}
        >
          주문 취소 요청하기
        </CancelButton>
      </ModalFooter>
    </ModalContainer>
  );
};

export default CancelOrderContainer;
